import { useMemo } from "react";

import { useTranslate } from "@similarweb/i18n-provider";

import { ALL_TAB_CATALOG_SUMMARY_SEARCH_LIMIT } from "app/components/React/QuickSearch/config";
import { filterQuickSearchCatalogBySummary } from "app/components/React/QuickSearch/helpers/filterQuickSearchCatalogBySummary";
import type { QuickSearchCatalogPage } from "app/components/React/QuickSearch/pageData/getQuickSearchCatalogPages";
import { useAccessibleQuickSearchCatalogPages } from "app/components/React/QuickSearch/pageData/useAccessibleQuickSearchCatalogPages";

export type ReportCatalogSearchMatchState = {
    trimmedSearch: string;
    isSearching: boolean;
    reportCatalogMatches: QuickSearchCatalogPage[];
    hasReportSearchHits: boolean;
};

export const getReportCatalogSearchMatchState = (
    searchQuery: string,
    catalogPages: QuickSearchCatalogPage[],
): ReportCatalogSearchMatchState => {
    const trimmedSearch = searchQuery.trim();
    const isSearching = trimmedSearch.length > 0;
    const reportCatalogMatches = isSearching
        ? filterQuickSearchCatalogBySummary(catalogPages, trimmedSearch, ALL_TAB_CATALOG_SUMMARY_SEARCH_LIMIT)
        : [];

    return {
        trimmedSearch,
        isSearching,
        reportCatalogMatches,
        hasReportSearchHits: isSearching && reportCatalogMatches.length > 0,
    };
};

export const useReportCatalogSearchMatches = (searchQuery: string): ReportCatalogSearchMatchState => {
    const { getLocale } = useTranslate();
    const locale = getLocale();
    const accessibleCatalogPages = useAccessibleQuickSearchCatalogPages(locale);

    return useMemo(
        () => getReportCatalogSearchMatchState(searchQuery, accessibleCatalogPages),
        [accessibleCatalogPages, searchQuery],
    );
};
